import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { ModuleState, PolicyAppState, PolicyAppStateModules, SectionMask, SectionState } from './types';
import { ModuleId, TreePath, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { initialState } from './slice';

// SELECTORS
export const getPhantom = (state: PolicyAppState): boolean => state.phantom;
export const getIsPolicyDataLoaded = (state: PolicyAppState): boolean => state.isPolicyDataLoaded;
export const getModules = (state: PolicyAppState): PolicyAppStateModules => state.modules;
export const getModuleState = (state: PolicyAppState, moduleId: ModuleId): ModuleState | undefined =>
  state.modules[moduleId];
export const getSectionValidity = (state: PolicyAppState, path: TreePath): boolean =>
  state.modules[path.moduleId]?.sections[path.sectionId].isValid ?? false;
export const getSectionMasks = (state: PolicyAppState, path: TreePath): SectionMask[] =>
  state.modules[path.moduleId]?.sections[path.sectionId].masks ?? [];

export const getInvalidTreePaths = (state: PolicyAppState): Array<TreePath> => {
  const invalidPaths: Array<TreePath> = [];

  Object.entries(state.modules).forEach(([moduleId, moduleState]: [ModuleId, ModuleState]) => {
    const sections = moduleState.sections;

    Object.entries(sections).forEach(([sectionId, sectionState]: [SectionId, SectionState]) => {
      if (!sectionState.isValid) {
        invalidPaths.push({
          moduleId: moduleId as ModuleId,
          sectionId: sectionId as SectionId,
        });
      }
    });
  });

  return invalidPaths;
};

// ACTIONS
export enum PoliciesReduxActions {
  RESET_STATE = 'resetState',
  SET_SECTION_VALIDITY = 'setSectionValidity',
  SET_POLICY_DATA_LOADED = 'setPolicyDataLoaded',
  SET_INITIAL_TREE_STATE = 'setInitialTreeState',

  // navigation
  SET_NAVIGATION_HEADER_SLICE = 'setNavigationHeaderSlice',
  SET_POLICY_NAME = 'setPolicyName',

  // masks
  SET_SECTION_MASK = 'setSectionMask',
  CLEAR_SECTION_MASK = 'clearSectionMask',

  // save button
  SET_IS_LOADING = 'setIsLoading',
  SET_IS_SAVING = 'setIsSaving',
  SET_SAVE_RESTRICTION = 'setSaveRestriction',
  SET_IS_WAITING_VALIDATION = 'setIsWaitingValidation',

  // status
  SET_IS_STATUS_FULL_UPDATE_NEEDED = 'setIsStatusFullUpdateNeeded',
  SET_IS_STATUS_PATH_UPDATE_NEEDED = 'setIsStatusPathUpdateNeeded',
  SET_PATH_TO_BE_UPDATED_WITH_STATUS = 'setPathToBeUpdatedWithStatus',
  SET_PATH_STATUSES = 'setPathStatuses',

  // custom patch management actions
  SET_IS_LOADING_FOR_PATCH_SUMMARY_WINDOW = 'setIsLoadingForPatchSummaryWindow',
  SET_SELECTED_IDS_FOR_PATCH_SUMMARY_WINDOW = 'setSelectedIdsForPatchSummaryWindow',
  SET_AVAILABLE_MAINTENANCE_WINDOWS_LOADED_FOR_PATCH = 'setAvailableMaintenanceWindowsLoadedForPatch',

  // custom integrity monitor actions
  SET_COMPANY_RULE_SETS_LOADED_FOR_INTEGRITY_MONITOR = 'setCompanyRuleSetsLoadedForIntegrityMonitor',
  SET_SELECTED_RULE_SETS_LOADED_FOR_INTEGRITY_MONITOR = 'setSelectedRuleSetsLoadedForIntegrityMonitor',

  // custom content control actions
  SET_AVAILABLE_SCHEDULERS_LOADED_FOR_CONTENT_CONTROL_WEB = 'setAvailableSchedulersLoadedForContentControlWeb',

  // custom antimalware actions
  SET_EXCLUSIONS_LISTS_LOADED_FOR_ANTIMALWARE_SETTINGS = 'setExclusionsListsLoadedForAntimalwareSettings',
  SET_VENDORS_EXCLUSIONS_LISTS_LOADED_FOR_ANTIMALWARE_SETTINGS = 'setVendorsExclusionsListsLoadedForAntimalwareSettings',

  //custom general actions
  SET_POLICY_VALIDITY_LOADED_FOR_GENERAL_DETAILS = 'setPolicyValidityLoadedForGeneralDetails',
  SET_SERVER_URL_LOADED_FOR_GENERAL_SECURITY_TELEMTRY = 'setServerUrlLoadedForGeneralSecurityTelemetry',
}

export const reset = createAction(PoliciesReduxActions.RESET_STATE);
export const resetActionHandler = (state: PolicyAppState): void => {
  state.navigationHeaderSlice = initialState.navigationHeaderSlice;
  state.phantom = initialState.phantom;
  state.modules = initialState.modules;
  state.isPolicyDataLoaded = initialState.isPolicyDataLoaded;
  state.saveButtonSlice = initialState.saveButtonSlice;
  state.accordionsSlice = initialState.accordionsSlice;
};

export const setPolicyDataLoaded = createAction<boolean>(PoliciesReduxActions.SET_POLICY_DATA_LOADED);
export const setPolicyDataLoadedActionHandler = (state: PolicyAppState, action: PayloadAction<boolean>): void => {
  state.isPolicyDataLoaded = action.payload;
  state.saveButtonSlice.isLoading = !action.payload;
};

export const setSectionValidity = createAction<{ path: TreePath; value: boolean }>(
  PoliciesReduxActions.SET_SECTION_VALIDITY
);
export const setSectionValidityActionHandler = (
  state: PolicyAppState,
  action: PayloadAction<{ path: TreePath; value: boolean }>
): void => {
  const moduleId = action.payload.path.moduleId;
  const sectionId = action.payload.path.sectionId;
  const module = state.modules[moduleId];

  if (!module) {
    return;
  }

  module.sections[sectionId].isValid = action.payload.value;
};

export const setInitialTreeState = createAction<{ modules: PolicyAppStateModules; phantom: boolean }>(
  PoliciesReduxActions.SET_INITIAL_TREE_STATE
);
export const setInitialTreeStateActionHandler = (
  state: PolicyAppState,
  action: PayloadAction<{ modules: PolicyAppStateModules; phantom: boolean }>
): void => {
  state.phantom = action.payload.phantom;
  state.modules = action.payload.modules;
};

export const setSectionMask = createAction<{ path: TreePath; mask: SectionMask }[]>(
  PoliciesReduxActions.SET_SECTION_MASK
);
export const setSectionMaskActionHandler = (
  state: PolicyAppState,
  action: PayloadAction<{ path: TreePath; mask: SectionMask }[]>
): void => {
  action.payload.forEach(config => {
    const moduleId = config.path.moduleId;
    const sectionId = config.path.sectionId;
    const module = state.modules[moduleId];

    if (!module) {
      return;
    }

    if (sectionId !== SectionId.ALL && !module.sections[sectionId]) {
      return;
    }

    // method to set mask to section
    const setMaskToSectionFn = (id: SectionId) => {
      if (!module.sections[id].masks) {
        module.sections[id].masks = [config.mask];
        return;
      }

      // skip if mask already exists
      if (module.sections[id].masks.includes(config.mask)) {
        return;
      }

      module.sections[id].masks.push(config.mask);
      module.sections[id].masks.sort();
    };

    // set mask to all sections
    if (sectionId === SectionId.ALL) {
      Object.keys(module.sections).forEach((id: SectionId) => {
        setMaskToSectionFn(id);
      });

      return;
    }

    setMaskToSectionFn(sectionId);
  });
};

export const clearSectionMask = createAction<{ path: TreePath; mask: SectionMask }[]>(
  PoliciesReduxActions.CLEAR_SECTION_MASK
);
export const clearSectionMaskActionHandler = (
  state: PolicyAppState,
  action: PayloadAction<{ path: TreePath; mask: SectionMask }[]>
): void => {
  action.payload.forEach(config => {
    const moduleId = config.path.moduleId;
    const sectionId = config.path.sectionId;
    const module = state.modules[moduleId];

    if (!module) {
      return;
    }

    if (sectionId !== SectionId.ALL && (!module.sections[sectionId] || !module.sections[sectionId].masks)) {
      return;
    }

    // method to set mask to section
    const clearMaskSectionFn = (id: SectionId) => {
      // skip if mask does not exist
      if (!module.sections[id].masks.includes(config.mask)) {
        return;
      }

      module.sections[id].masks = module.sections[id].masks.filter((mask: SectionMask) => mask !== config.mask);
    };

    // clear mask for all sections
    if (sectionId === SectionId.ALL) {
      Object.keys(module.sections).forEach((id: SectionId) => {
        clearMaskSectionFn(id);
      });

      return;
    }

    clearMaskSectionFn(sectionId);
  });
};
